import React, {
  CSSProperties,
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useMemo,
} from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import UserProfileDropdown from './UserProfileDropdown';
import { LAYOUT_CONSTANTS } from '../hooks/useLayout';
import { useNavigationHistory } from '@/shared/context/useNavigationHistory';

interface BreadcrumbItem {
  label: string;
  path: string;
  navigateTo: string;
}

const MenuIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M4 5L20 5M4 11H20M4 17H12"
      stroke="#474A4C"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const routeLabels: Record<string, string> = {
  '/': 'Panel główny',
  '/Panel-główny': 'Panel główny',
  '/administrators': 'Administratorzy',
  '/buildings': 'Klienci',
  '/Klienci': 'Klienci',
  '/technicians': 'Technicy',
  '/subscriptions': 'Subskrypcje',
  '/activities': 'Aktywności',
  '/qms': 'QMS',
  '/Profil': 'Profil',
  '/Placówki': 'Placówki',
  '/Placówki/Dodaj-placówkę': 'Dodaj placówkę',
  '/Typy-usług': 'Typy usług',
  '/Typy-usług/Dodaj-typ-usługi': 'Dodaj typ usługi',
  '/Certyfikaty': 'Certyfikaty',
  '/Moje-certyfikaty': 'Certyfikaty',
};

// Segment-level labels for breadcrumb generation
const segmentLabels: Record<string, string> = {
  administrators: 'Administratorzy',
  addUser: 'Dodaj Administratora',
  'Edycja-Administratora': 'Edytuj Administratora',
  'Podgląd-Administratora': 'Podgląd Administratora',
  buildings: 'Klienci',
  Klienci: 'Klienci',
  'Dodaj-klienta': 'Dodaj klienta',
  'Podgląd-klienta': 'Podgląd klienta',
  'Edycja-klienta': 'Edytuj klienta',
  'Edytuj-klienta': 'Edytuj klienta',
  technicians: 'Technicy',
  addTechnician: 'Dodaj technika',
  'Podgląd-technika': 'Podgląd technika',
  'Edycja-technika': 'Edycja technika',
  'Zobacz-kalendarz': 'Zobacz kalendarz',
  subscriptions: 'Subskrypcje',
  activities: 'Aktywności',
  qms: 'QMS',
  Profil: 'Profil',
  'Edytuj-profil': 'Edytuj profil',
  'Panel-główny': 'Panel główny',
  Placówki: 'Placówki',
  'Dodaj-placówkę': 'Dodaj placówkę',
  'Edycja-placówki': 'Edycja placówki',
  'Podgląd-placówki': 'Podgląd placówki',
  Plany: 'Plany',
  'Utwórz-plan': 'Utwórz plan',
  'Podgląd-planu': 'Podgląd planu',
  'Edycja-planu': 'Edytuj plan',
  'Edytuj-ofertę': 'Edytuj ofertę',
  'Podgląd-oferty': 'Podgląd oferty',
  'Utwórz-ofertę': 'Utwórz ofertę',
  'Typy-usług': 'Typy usług',
  'Dodaj-typ-usługi': 'Dodaj typ usługi',
  'Podgląd-typu-usługi': 'Podgląd typu usługi',
  'Edycja-typu-usługi': 'Edytuj typ usługi',
  'Zgłoszenia-kontaktowe': 'Zgłoszenia kontaktowe',
  'Utwórz-zgłoszenie-kontaktowe': 'Utwórz zgłoszenie kontaktowe',
  Zgłoszenia: 'Zgłoszenia',
  'Moje-zadania': 'Moje zadania',
  'Podgląd-zgłoszenia': 'Podgląd zgłoszenia serwisowego',
  'formularz-zadania': 'Wypełnij formularz zadania',
  'Podgląd-mojego-zadania': 'Podgląd zgłoszenia serwisowego',
  'Podgląd-zgłoszenia-kontaktowego': 'Podgląd zgłoszenia kontaktowego',
  'Podgląd-mojego-zadania-kontaktowego': 'Podgląd zgłoszenia kontaktowego',
  'Produkty i usługi': 'Produkty i usługi',
  'Formularze-zadań': 'Formularze zadań',
  'Utwórz-formularz': 'Utwórz formularz zadania',
  'Podgląd-Formularza': 'Podgląd formularza zadania',
  'Edytuj-Formularz': 'Edytuj formularz zadania',
  Certyfikaty: 'Certyfikaty',
  'Moje-certyfikaty': 'Certyfikaty',
  'Podgląd-certyfikatu': 'Podgląd certyfikatu',
  'Podgląd-szablonu': 'Podgląd szablonu certyfikatu',
  'Utwórz-szablon-certyfikatu': 'Utwórz szablon certyfikatu',
  'Edycja-szablonu': 'Edytuj szablon certyfikatu',
};

// Maps a parent path to the label used for any unknown (dynamic) child segment.
// Use this when a route like /Parent/:dynamicParam should always show a fixed
// label for the dynamic part instead of the raw URL value.
const dynamicChildLabels: Record<string, string> = {
  '/Produkty i usługi': 'Zamów usługę',
  '/Moje-certyfikaty': 'Podgląd certyfikatu',
  '/Przesłane-formularze': 'Podgląd przesłanego formularza',
};

// Segments that are part of URL structure but should not appear as breadcrumb items
const skippedSegments = new Set(['krok-1', 'krok-2', 'krok-3', 'krok-4']);

const getSection = (pathname: string) => pathname.split('/')[1] || '';

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const isDynamicId = (segment: string | undefined): boolean =>
  Boolean(segment) &&
  (UUID_RE.test(segment as string) ||
    /^[0-9a-f-]{36}$/i.test(segment as string));

/**
 * The role of a page within its section — used to keep List/View/Edit/Add as
 * distinct breadcrumb entries even though they live in the same URL section.
 * Returns 'list' for the section root (e.g. /administrators), and the role
 * segment otherwise (e.g. 'Podgląd-Administratora', 'Edycja-Administratora',
 * 'addUser'). Two entries with the same section AND role are considered the
 * same conceptual page (e.g. viewing two different users) and are collapsed.
 *
 * Most sections follow /section/role/:id, but some (e.g. Placówki) put the id
 * before the role (/section/:id/role). When the segment after the section
 * looks like a UUID, the role is read from the segment after that.
 */
const getPageRole = (pathname: string): string => {
  const segs = pathname.split('/').filter(Boolean);
  if (segs.length <= 1) {
    return 'list';
  }
  if (isDynamicId(segs[1])) {
    return segs[2] ?? 'list';
  }
  return segs[1] as string;
};

/**
 * Builds breadcrumbs from the full navigation history stack.
 * Consecutive entries representing the same conceptual page (same section AND
 * same page role) are collapsed to the deepest path, then breadcrumbs are
 * generated and concatenated across role/section changes. This preserves
 * trails like List → View → Edit while still collapsing list-pagination or
 * same-role transitions between two records.
 */
const buildBreadcrumbsFromStack = (
  stack: { pathname: string; search?: string }[]
): BreadcrumbItem[] => {
  // Step 1: collapse consecutive entries that share both section and role,
  // keeping only the deepest path. List → View → Edit have different roles,
  // so each is preserved as its own crumb.
  // Each collapsed entry stores { pathname, search } so query params are preserved.
  const collapsed: { pathname: string; search: string }[] = [];
  for (const entry of stack) {
    const last = collapsed[collapsed.length - 1];
    const sameAsLast =
      last !== undefined &&
      getSection(entry.pathname) === getSection(last.pathname) &&
      getPageRole(entry.pathname) === getPageRole(last.pathname);
    if (sameAsLast) {
      collapsed[collapsed.length - 1] = {
        pathname: entry.pathname,
        search: entry.search ?? '',
      };
    } else {
      collapsed.push({ pathname: entry.pathname, search: entry.search ?? '' });
    }
  }

  // Build a set of all pathnames directly visited (used to detect cross-section jumps)
  const visitedPathnames = new Set(stack.map((e) => e.pathname));

  // Step 2: generate breadcrumbs per collapsed entry.
  // - Section already seen earlier → skip its parent crumb (avoid duplication).
  // - Cross-section jump (section root never visited) → show only the leaf crumb,
  //   not the section parent (e.g. jumped to /Plany/Podgląd-planu directly from
  //   /Klienci/Podgląd-klienta → show only "Podgląd planu", not "Plany > Podgląd planu").
  const seenSections = new Set<string>();
  const result = collapsed.flatMap(({ pathname, search }) => {
    const fullPath = pathname + search;
    const section = getSection(pathname);
    const sectionRoot = `/${section}`;
    const crumbs = generateBreadcrumbs(fullPath);

    let included: BreadcrumbItem[];
    if (seenSections.has(section)) {
      // Section already shown — drop its parent crumb
      included = crumbs.slice(1);
    } else if (pathname !== sectionRoot && !visitedPathnames.has(sectionRoot)) {
      // Cross-section jump — drop the section root (never visited) but keep
      // all intermediate crumbs so deep paths like SR-view > fill-form show
      // their full ancestry rather than just the leaf.
      // Guard: if slicing would produce nothing (e.g. a UUID-only path whose
      // only crumb is the section), keep the full list rather than returning [].
      included = crumbs.slice(1).length > 0 ? crumbs.slice(1) : crumbs;
    } else {
      included = crumbs;
    }
    seenSections.add(section);
    // The leaf crumb navigates to the full stack path (preserves UUID + search).
    // Parent crumbs use their own partial path (e.g. /Klienci for the list).
    return included.map((crumb, i) => ({
      ...crumb,
      navigateTo: i === included.length - 1 ? fullPath : crumb.path,
    }));
  });
  return result.length > 0
    ? result
    : [{ label: 'Panel główny', path: '/', navigateTo: '/' }];
};

/**
 * Generates breadcrumb items from a pathname
 */
const generateBreadcrumbs = (pathname: string): BreadcrumbItem[] => {
  // Remove query params and decode URL
  const queryIndex = pathname.indexOf('?');
  const pathWithoutQuery =
    queryIndex !== -1 ? pathname.substring(0, queryIndex) : pathname;
  const decodedPath = decodeURIComponent(pathWithoutQuery);

  // Split into segments and filter out empty strings
  const segments = decodedPath
    .split('/')
    .filter((seg): seg is string => Boolean(seg));

  if (segments.length === 0) {
    return [{ label: 'Panel główny', path: '/', navigateTo: '/' }];
  }

  const breadcrumbs: BreadcrumbItem[] = [];

  // Build breadcrumbs by accumulating path segments
  let currentPath = '';
  for (const segment of segments) {
    if (!segment) {
      continue;
    }

    currentPath += `/${segment}`;

    // Skip dynamic segments (UUIDs, IDs, etc.)
    // Check if segment looks like a UUID or ID (contains only hex chars or is very long)
    const isDynamicSegment =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
        segment
      ) || /^[0-9a-f-]{36}$/i.test(segment);

    if (isDynamicSegment || skippedSegments.has(segment)) {
      if (isDynamicSegment) {
        const parentPath = currentPath.substring(
          0,
          currentPath.lastIndexOf('/')
        );
        const dynamicLabel = dynamicChildLabels[parentPath];
        if (dynamicLabel) {
          // Push a named crumb for this UUID segment using the parent-scoped label.
          breadcrumbs.push({
            label: dynamicLabel,
            path: currentPath,
            navigateTo: currentPath,
          });
        } else if (breadcrumbs.length > 0) {
          // Backfill the UUID into the last pushed crumb so clicking it navigates
          // to the correct URL (e.g. /Zgłoszenia/Podgląd-zgłoszenia/{uuid} not
          // /Zgłoszenia/Podgląd-zgłoszenia).
          const last = breadcrumbs[breadcrumbs.length - 1];
          if (last) {
            last.path = currentPath;
            last.navigateTo = currentPath;
          }
        }
      }
      continue;
    }

    // When building the /Zgłoszenia parent crumb from a contact-request or
    // my-task detail path, point it to the matching tab so the back-link lands
    // correctly.
    const isMyContactTaskDetail = decodedPath.includes(
      'Podgląd-mojego-zadania-kontaktowego'
    );
    const isContactRequestDetail =
      !isMyContactTaskDetail &&
      decodedPath.includes('Podgląd-zgłoszenia-kontaktowego');
    const isMyTaskDetail =
      !isMyContactTaskDetail && decodedPath.includes('Podgląd-mojego-zadania');
    let crumbPath = currentPath;
    if (currentPath === '/Zgłoszenia') {
      if (isMyContactTaskDetail) {
        crumbPath = '/Zgłoszenia?tab=moje-kontaktowe';
      } else if (isContactRequestDetail) {
        crumbPath = '/Zgłoszenia?tab=kontaktowe';
      } else if (isMyTaskDetail) {
        crumbPath = '/Zgłoszenia?tab=moje-zadania';
      }
    }

    // Try to get label from full path first (for exact matches)
    const fullPathLabel = routeLabels[currentPath];
    if (fullPathLabel) {
      breadcrumbs.push({
        label: fullPathLabel,
        path: crumbPath,
        navigateTo: crumbPath,
      });
    } else {
      // Fall back to segment label, then to a parent-scoped dynamic label,
      // then to the raw segment value.
      const parentPath = currentPath.substring(0, currentPath.lastIndexOf('/'));
      const segmentLabel =
        segmentLabels[segment] || dynamicChildLabels[parentPath] || segment;
      breadcrumbs.push({
        label: segmentLabel,
        path: crumbPath,
        navigateTo: crumbPath,
      });
    }
  }

  return breadcrumbs.length > 0
    ? breadcrumbs
    : [{ label: 'Panel główny', path: '/', navigateTo: '/' }];
};

export type LayOutHeaderType = {
  headerStyle: CSSProperties;
  isCollapsed: boolean;
  toggleCollapse: () => void;
};

const LayOutHeader: React.FC<LayOutHeaderType> = ({
  headerStyle,
  toggleCollapse,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { stack } = useNavigationHistory();
  const decodedPathname = decodeURIComponent(location.pathname);
  // Memoized so the array identity is stable across re-renders that don't
  // touch navigation (e.g. the truncation-measurement state below) — an
  // unstable reference here would make the "breadcrumbs changed" effect
  // dependency fire on every render instead of only on real navigation.
  const breadcrumbs = useMemo<BreadcrumbItem[]>(
    () =>
      decodedPathname === '/Panel-główny'
        ? [
            {
              label: 'Panel główny',
              path: '/Panel-główny',
              navigateTo: '/Panel-główny',
            },
          ]
        : decodedPathname === '/Profil/Edytuj-profil'
          ? [
              { label: 'Profil', path: '/Profil', navigateTo: '/Profil' },
              {
                label: 'Edytuj profil',
                path: '/Profil/Edytuj-profil',
                navigateTo: '/Profil/Edytuj-profil',
              },
            ]
          : decodedPathname.startsWith('/Profil')
            ? [{ label: 'Profil', path: '/Profil', navigateTo: '/Profil' }]
            : buildBreadcrumbsFromStack(stack),
    [decodedPathname, stack]
  );

  const [showMiddlePopover, setShowMiddlePopover] = useState(false);
  const middlePopoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setShowMiddlePopover(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!showMiddlePopover) {
      return;
    }
    const handleClickOutside = (e: MouseEvent) => {
      if (
        middlePopoverRef.current &&
        !middlePopoverRef.current.contains(e.target as Node)
      ) {
        setShowMiddlePopover(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showMiddlePopover]);

  const breadcrumbContainerRef = useRef<HTMLDivElement>(null);
  const [shouldTruncate, setShouldTruncate] = useState(false);

  // Whenever the breadcrumb trail changes, or the space available for it
  // changes (sidenav collapse/expand, window resize), optimistically try
  // showing the full trail — the effect below corrects it to the "..."
  // truncated view only if it actually doesn't fit.
  useLayoutEffect(() => {
    const container = breadcrumbContainerRef.current;
    if (!container) {
      return;
    }
    setShouldTruncate(false);
    const resizeObserver = new ResizeObserver(() => setShouldTruncate(false));
    resizeObserver.observe(container);
    return () => resizeObserver.disconnect();
  }, [breadcrumbs]);

  // After a "try full" attempt, collapse to the truncated view only if the
  // full trail overflows the space actually available for it in the header.
  useLayoutEffect(() => {
    const container = breadcrumbContainerRef.current;
    if (!container || shouldTruncate) {
      return;
    }
    if (
      breadcrumbs.length > 2 &&
      container.scrollWidth > container.clientWidth
    ) {
      setShouldTruncate(true);
    }
  }, [breadcrumbs, shouldTruncate]);

  const firstCrumb = breadcrumbs[0];
  const lastCrumb = breadcrumbs[breadcrumbs.length - 1];
  const middleCrumbs = shouldTruncate ? breadcrumbs.slice(1, -1) : [];

  const handleBreadcrumbClick = (crumb: BreadcrumbItem, isLast: boolean) => {
    if (!isLast) {
      const targetBasePath = crumb.navigateTo.split('?')[0];
      if (crumb.navigateTo === '/administrators') {
        navigate('/administrators?page=1&limit=10', { replace: true });
      } else if (
        targetBasePath === '/Zgłoszenia' &&
        decodedPathname.includes('Podgląd-zgłoszenia-kontaktowego')
      ) {
        navigate('/Zgłoszenia?tab=kontaktowe');
      } else {
        navigate(crumb.navigateTo);
      }
    }
  };

  return (
    <>
      <div
        className="fixed top-0 transition-all duration-300 flex items-center justify-between bg-white"
        style={{
          ...headerStyle,
          height: LAYOUT_CONSTANTS.HEADER_HEIGHT,
          zIndex: 40,
          paddingTop: 4,
          paddingBottom: 4,
          paddingLeft: 4,
          paddingRight: 20,
        }}
      >
        <div className="flex items-center flex-1 min-w-0">
          <div className="flex items-center">
            <button
              onClick={toggleCollapse}
              className="w-12 h-12 flex items-center justify-center cursor-pointer"
              aria-label="Toggle menu"
            >
              <div className="w-10 overflow-hidden rounded-xl flex flex-col items-center justify-center">
                <div className="self-stretch h-10 flex items-center justify-center">
                  <MenuIcon />
                </div>
              </div>
            </button>
          </div>
          <div
            ref={breadcrumbContainerRef}
            className="flex flex-col items-start flex-1 min-w-0 overflow-hidden"
          >
            <div className="px-3 py-1.5 flex items-center justify-center">
              <div className="self-stretch rounded flex items-center justify-center">
                <div className="flex items-center justify-center gap-1">
                  <div className="flex items-center gap-2 text-[#474A4C] text-xs font-normal leading-4 tracking-[0.4px] whitespace-nowrap">
                    {shouldTruncate && firstCrumb && lastCrumb ? (
                      <>
                        {/* First crumb */}
                        <button
                          type="button"
                          onClick={() =>
                            handleBreadcrumbClick(firstCrumb, false)
                          }
                          data-testid={`breadcrumb-${firstCrumb.label}`}
                          className="cursor-pointer text-primary-color-40"
                        >
                          {firstCrumb.label}
                        </button>
                        <ChevronRightIcon className="w-4 h-4 mx-1 text-on-surface-variant" />

                        {/* Ellipsis button with popover */}
                        <div className="relative" ref={middlePopoverRef}>
                          <button
                            type="button"
                            onClick={() => setShowMiddlePopover((v) => !v)}
                            className="cursor-pointer text-primary-color-40 px-1"
                            aria-label="Pokaż ukryte elementy ścieżki"
                          >
                            ...
                          </button>
                          {showMiddlePopover && (
                            <div className="absolute top-full left-0 mt-0.5 z-50">
                              {/* Arrow caret — top-left, pointing up */}
                              <div
                                style={{ paddingLeft: 16, paddingRight: 16 }}
                              >
                                <div
                                  style={{
                                    width: 0,
                                    height: 0,
                                    borderLeft: '8px solid transparent',
                                    borderRight: '8px solid transparent',
                                    borderBottom: '8px solid #181A1B',
                                  }}
                                />
                              </div>
                              {/* Tooltip body */}
                              <div
                                style={{
                                  background: '#181A1B',
                                  borderRadius: 8,
                                  padding: 8,
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: 4,
                                  minWidth: 'max-content',
                                }}
                              >
                                {middleCrumbs.map((crumb, i) => (
                                  <button
                                    key={i}
                                    type="button"
                                    onClick={() => {
                                      setShowMiddlePopover(false);
                                      handleBreadcrumbClick(crumb, false);
                                    }}
                                    style={{
                                      color: 'white',
                                      fontSize: 12,
                                      fontFamily: 'Roboto',
                                      fontWeight: 400,
                                      lineHeight: '16px',
                                      letterSpacing: 0.4,
                                      wordWrap: 'break-word',
                                      textAlign: 'left',
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    {crumb.label}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                        <ChevronRightIcon className="w-4 h-4 mx-1 text-on-surface-variant" />

                        {/* Last crumb (current page) */}
                        <button
                          type="button"
                          disabled
                          data-testid={`breadcrumb-${lastCrumb.label}`}
                          className="cursor-default"
                        >
                          {lastCrumb.label}
                        </button>
                      </>
                    ) : (
                      breadcrumbs.map((crumb, index) => {
                        const isLast = index === breadcrumbs.length - 1;
                        return (
                          <React.Fragment key={index}>
                            <button
                              type="button"
                              onClick={() =>
                                handleBreadcrumbClick(crumb, isLast)
                              }
                              disabled={isLast}
                              data-testid={`breadcrumb-${crumb.label}`}
                              className={`${
                                isLast
                                  ? 'cursor-default'
                                  : 'cursor-pointer text-primary-color-40'
                              }`}
                            >
                              {crumb.label}
                            </button>
                            {index < breadcrumbs.length - 1 && (
                              <ChevronRightIcon className="w-4 h-4 mx-1 text-on-surface-variant" />
                            )}
                          </React.Fragment>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <UserProfileDropdown />
      </div>
    </>
  );
};

export default LayOutHeader;
